/**
 * The screen is showing the demo of a feature above the plan (#2816): the
 * flag it is sold by, for everything below that renders actions. Reading,
 * filtering and exporting the demo work; what the closed feature would write
 * or pay for (FAQ draft, theme edits, a client message, a CRM task) stays
 * closed, and a disabled control says which plan opens it.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { minPlanLabel } from '@/lib/product-package-label';

interface LockedDemoValue {
  flag: keyof FeatureFlags | null;
  asked: boolean;
  declined: boolean;
}

const LockedDemoContext = createContext<LockedDemoValue>({
  flag: null,
  asked: false,
  declined: false,
});

export function LockedDemoProvider({
  flag,
  active = true,
  asked = active,
  declined = false,
  children,
}: {
  flag: keyof FeatureFlags;
  /** Off — the screen is open on the plan's own data: no provider value. */
  active?: boolean;
  /**
   * The requests below go as the demo's (`useLockedDemoAsked`) — by the
   * client's verdict. Defaults to `active`; it differs when the server answered
   * the demo to a screen the client still thinks open (a plan change with the
   * app open): the screen is drawn as the demo, its requests keep their keys.
   */
  asked?: boolean;
  /**
   * The effective filter of the screen's requests excludes the demo
   * (`includeDemo=no`, #2816): a read that has no filter of its own (the
   * theme set) sends it too, so it is refused as the screen's other reads are.
   */
  declined?: boolean;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ flag: active ? flag : null, asked, declined }),
    [active, flag, asked, declined]
  );
  return (
    <LockedDemoContext.Provider value={value}>{children}</LockedDemoContext.Provider>
  );
}

/** The flag of the closed feature whose demo is on screen, or `null` on an ordinary screen. */
export function useLockedDemoFlag(): keyof FeatureFlags | null {
  return useContext(LockedDemoContext).flag;
}

/**
 * The screen asks for the demo of a closed feature (#2816) — the `'demo'`
 * part of its query keys, apart from the organization's own answers.
 */
export function useLockedDemoAsked(): boolean {
  return useContext(LockedDemoContext).asked;
}

/** The screen's requests decline the demo (`includeDemo=no`) — see `LockedDemoProvider`. */
export function useLockedDemoDeclined(): boolean {
  return useContext(LockedDemoContext).declined;
}

/** «Available on Pro and up» — what a closed action says, by the flag it is sold with. */
export function useClosedActionReason(flag: keyof FeatureFlags): string {
  const { t } = useTranslation('common');
  const plan = minPlanLabel(flag);
  return plan
    ? t('featureGate.demo.actionClosed', { plan })
    : t('featureGate.tier.titleGeneric');
}

/**
 * Why an action is closed while the demo is on screen, or `undefined` on an
 * ordinary screen (the action works as always).
 */
export function useLockedDemoReason(): string | undefined {
  const flag = useLockedDemoFlag();
  // The reason is read for a flag even on an ordinary screen (hooks do not
  // branch); the context decides whether it applies.
  const reason = useClosedActionReason(flag ?? 'enableDealStateSummary');
  return flag ? reason : undefined;
}
